/**
 * RUM phase 1: initial-load milestones on the `performance.timeOrigin` clock,
 * one beacon per page load to `POST /v1/rum`, sampled (see sampling.ts).
 * `installRum(router)` once from `main.tsx`; `recordRumMilestone(...)`
 * wherever a milestone happens. Runs alongside the legacy
 * `PerformanceProvider` until validated.
 * Design: `docs/proposals/real-user-monitoring.md`.
 */
import { createRumSession } from './session';
import type { RumRouter } from './session';
import { createRumRequestQueue } from './requests';
import type { RumRequestTiming } from './requests';
import { sendRumBeacon } from './beacon';
import { getRumContext } from './context';
import { isRumSessionSampled } from './sampling';
import { RumMilestone } from './types';

const session = createRumSession({ send: sendRumBeacon, getContext: getRumContext });

const requestQueue = createRumRequestQueue({
  send: sendRumBeacon,
  getContext: getRumContext,
  getSessionId: () => session.sessionId,
  getInitialLoadSentAt: session.sentAt,
});

export function installRum(router: RumRouter): void {
  // Unsampled loads never install: no timers, no listeners, no beacon.
  // Milestones still record into the session harmlessly.
  if (!isRumSessionSampled()) {
    return;
  }
  session.install(router);
  requestQueue.install(router);
}

export const recordRumMilestone = session.recordMilestone;

/**
 * Feed one settled API request into the requests beacon. Wired as the
 * `onRequestTiming` hook on every `APIClient` instance (`services/api/*`).
 * No-op on unsampled page loads.
 */
export function recordRumRequest(timing: RumRequestTiming): void {
  requestQueue.record(timing);
}

/** Milestones embedded apps may report through the window bridge. */
const EMBEDDED_APP_MILESTONES: ReadonlySet<RumMilestone> = new Set([RumMilestone.ChildAppInteractive]);

function toEmbeddedMilestone(name: string): RumMilestone | null {
  for (const milestone of EMBEDDED_APP_MILESTONES) {
    if (milestone === name) {
      return milestone;
    }
  }
  return null;
}

/**
 * The `window.__hydra__.rum` surface (wired in utils/inject-globals). Embedded
 * apps call `expectMilestone` early in boot — the beacon then waits for the
 * matching `recordMilestone` (or the hard cap) before flushing — and
 * `recordMilestone` when the moment happens. Names outside the embedded
 * allowlist are ignored; old app builds that never call it change nothing.
 */
export const rumEmbeddedBridge = {
  expectMilestone(name: string): void {
    const milestone = toEmbeddedMilestone(name);
    if (milestone != null) {
      session.expectMilestone(milestone);
    }
  },
  recordMilestone(name: string): void {
    const milestone = toEmbeddedMilestone(name);
    if (milestone != null) {
      session.recordMilestone(milestone);
    }
  },
};

export { RumMilestone } from './types';
export type { RumInitialLoadPayload, RumRequestsPayload, RumSentReason } from './types';
export type { RumRequestTiming } from './requests';
